import { useRef, useCallback, useState, useEffect } from 'react';

/**
 * 쓰로틀링 훅
 *
 * @param {Function} callback - 쓰로틀링을 적용할 함수
 * @param {number} delay - 쓰로틀링 딜레이 시간 (밀리초, 기본값: 1000ms)
 * @returns {[Function, boolean]} [쓰로틀링이 적용된 함수, 쓰로틀링 상태]
 *
 * @example
 * const [handleClick, isThrottling] = useThrottle(() => {
 *   console.log('클릭됨');
 * }, 1000);
 *
 * <button onClick={handleClick} disabled={isThrottling}>클릭</button>
 */
export default function useThrottle(callback, delay = 1000) {
  const lastExecuteTimeRef = useRef(0);
  const [isThrottling, setIsThrottling] = useState(false);
  const timeoutRef = useRef(null);

  const throttledCallback = useCallback(
    (...args) => {
      const now = Date.now();

      if (now - lastExecuteTimeRef.current >= delay) {
        lastExecuteTimeRef.current = now;
        setIsThrottling(true);

        // 기존 타이머가 있으면 클리어
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // delay 시간 후에 쓰로틀링 상태 해제
        timeoutRef.current = setTimeout(() => {
          setIsThrottling(false);
        }, delay);

        callback(...args);
      }
    },
    [callback, delay]
  );

  // 컴포넌트 언마운트 시 타이머 클리어
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return [throttledCallback, isThrottling];
}
